import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './styles.css';

const PLACEMENTS = ['top', 'bottom', 'left', 'right'];

function getViewportOffset(rect, placement, offset) {
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollY = window.pageYOffset || document.documentElement.scrollTop;

  const top = rect.top + scrollY;
  const left = rect.left + scrollX;

  switch (placement) {
    case 'top':
      return {
        top: top - offset,
        left: left + rect.width / 2,
      };
    case 'bottom':
      return {
        top: top + rect.height + offset,
        left: left + rect.width / 2,
      };
    case 'left':
      return {
        top: top + rect.height / 2,
        left: left - offset,
      };
    case 'right':
    default:
      return {
        top: top + rect.height / 2,
        left: left + rect.width + offset,
      };
  }
}

function useIsomorphicLayoutEffect(effect, deps) {
  // Avoid SSR mismatch; choose the correct effect implementation without calling hooks conditionally.
  const useIso = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
  return useIso(effect, deps);
}

export const ToolTip = ({
  children,
  content,
  open: controlledOpen,
  onOpenChange,
  placement = 'top',
  offset = 8,
  className = '',
  style = {},
  delay = 100,
  closeDelay = 80,
  withArrow = true,
  ariaId,
  disabled = false,
  maskContent = false,
}) => {
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen != null;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = useCallback(
    (next) => {
      if (disabled) return;
      if (isControlled) {
        onOpenChange && onOpenChange(next);
      } else {
        setUncontrolledOpen(next);
      }
    },
    [isControlled, onOpenChange, disabled],
  );

  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [computedPlacement, setComputedPlacement] = useState(
    PLACEMENTS.includes(placement) ? placement : 'top',
  );

  const positionTooltip = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const base = getViewportOffset(rect, computedPlacement, offset);

    let nextTop = base.top;
    let nextLeft = base.left;

    if (computedPlacement === 'top' || computedPlacement === 'bottom') {
      // center horizontally, we'll translateX(-50%) in CSS
    } else {
      // center vertically, we'll translateY(-50%) in CSS
    }

    setCoords({ top: nextTop, left: nextLeft });
  }, [computedPlacement, offset]);

  useIsomorphicLayoutEffect(() => {
    if (!isOpen) return;
    positionTooltip();
  }, [isOpen, positionTooltip, content]);

  useEffect(() => {
    if (!isOpen) return;

    const onScrollOrResize = () => {
      positionTooltip();
    };

    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    let ro;
    if (triggerRef.current && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => positionTooltip());
      ro.observe(triggerRef.current);
    }

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      if (ro) ro.disconnect();
    };
  }, [isOpen, positionTooltip]);

  // Hover/focus interactions with delay
  const openTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    clearTimeout(closeTimeoutRef.current);
    openTimeoutRef.current = setTimeout(() => setOpen(true), delay);
  }, [delay, setOpen, disabled]);

  const handleClose = useCallback(() => {
    clearTimeout(openTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => setOpen(false), closeDelay);
  }, [closeDelay, setOpen]);

  useEffect(
    () => () => {
      clearTimeout(openTimeoutRef.current);
      clearTimeout(closeTimeoutRef.current);
    },
    [],
  );

  const clonedChild = useMemo(() => {
    const child = React.Children.only(children);
    const propsToInject = {
      ref: (node) => {
        triggerRef.current = node;
        if (typeof child.ref === 'function') child.ref(node);
        else if (child.ref) child.ref.current = node;
      },
      onMouseEnter: (e) => {
        child.props.onMouseEnter && child.props.onMouseEnter(e);
        handleOpen();
      },
      onMouseLeave: (e) => {
        child.props.onMouseLeave && child.props.onMouseLeave(e);
        handleClose();
      },
      onFocus: (e) => {
        child.props.onFocus && child.props.onFocus(e);
        setOpen(true);
      },
      onBlur: (e) => {
        child.props.onBlur && child.props.onBlur(e);
        setOpen(false);
      },
      'aria-describedby': ariaId,
    };
    return React.cloneElement(child, propsToInject);
  }, [children, handleOpen, handleClose, setOpen, ariaId]);

  const tooltipNode =
    isOpen && !disabled ? (
      <div
        ref={tooltipRef}
        role='tooltip'
        id={ariaId}
        className={`portal-tooltip portal-tooltip--${computedPlacement} ${className}`}
        style={{
          top: `${coords.top}px`,
          left: `${coords.left}px`,
          ...style,
        }}
        onMouseEnter={() => handleOpen()}
        onMouseLeave={() => handleClose()}
      >
        {withArrow && (
          <span
            className={`portal-tooltip__arrow portal-tooltip__arrow--${computedPlacement}`}
          />
        )}
        <div
          className='portal-tooltip__content'
          data-dd-privacy={maskContent ? 'mask' : ''}
        >
          {content}
        </div>
      </div>
    ) : null;

  return (
    <>
      {clonedChild}
      {tooltipNode ? createPortal(tooltipNode, document.body) : null}
    </>
  );
};
